/**
 * Current Campaigns — live GAM snapshot: which brands are delivering right now,
 * and which channels their ads delivered on over the trailing window.
 *
 * The backend caches one snapshot (refreshed at most daily): the GET returns it
 * instantly and auto-kicks a detached refresh when it's >24h old, so this tab
 * renders cached data immediately and polls while a refresh runs. Channel
 * attribution is DELIVERED-basis (yt_channel_id CMS report) — GAM has no
 * reliable "scheduled on channels" list for CMS/run-of-network targeted buys.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  EmptyState,
  KpiCard,
  LoadingDots,
  PACING_PILL,
  PacingBar,
  Spinner,
  StatusPill,
  tableStyles,
} from '../ui';
import { fmtCompact } from '../../utils/formatNumbers';
import {
  getCurrentCampaigns,
  refreshCurrentCampaigns,
  ChannelIndexEntry,
  CurrentCampaign,
  CurrentCampaignsBrand,
  CurrentCampaignsChannelIndex,
  CurrentCampaignsSnapshot,
} from '../../services/currentCampaignsApi';

/**
 * Hover text for the booked-goal cell — the deactivated/paused-line caveat and/or the
 * sponsorship Impression Limit notes (Sept 2026: GAM gives a sponsorship a %
 * goal, so its Impression Limit is the booked quantity and is folded into the
 * goal; a sponsorship with no limit adds no goal). undefined → no tooltip, no *.
 */
function goalCellTitle(c: {
  totalImpressionsGoal: number;
  pacingImpressionsGoal?: number;
  impressionLimitLineItems?: number;
  unlimitedSponsorshipLineItems?: number;
}): string | undefined {
  const parts: string[] = [];
  if (c.pacingImpressionsGoal != null && c.pacingImpressionsGoal < c.totalImpressionsGoal) {
    parts.push(
      `Pacing measured against ${c.pacingImpressionsGoal.toLocaleString()} — ` +
        `${(c.totalImpressionsGoal - c.pacingImpressionsGoal).toLocaleString()} booked units ` +
        `sit on deactivated or paused line items, which are not scheduled to deliver.`,
    );
  }
  if ((c.impressionLimitLineItems ?? 0) > 0) {
    parts.push(
      `Includes the GAM Impression Limit of ${c.impressionLimitLineItems} sponsorship line item(s) — ` +
        `GAM gives a sponsorship a % goal; the limit is the booked quantity.`,
    );
  }
  if ((c.unlimitedSponsorshipLineItems ?? 0) > 0) {
    parts.push(`${c.unlimitedSponsorshipLineItems} sponsorship line item(s) have no Impression Limit and add no goal.`);
  }
  return parts.length ? parts.join(' ') : undefined;
}

const POLL_MS = 20_000;
const CHANNEL_PILLS_COLLAPSED = 5;
const MAX_CHANNEL_RESULTS = 12;

/** Forgiving channel matching, mirroring the server's channelKey(): case-, apostrophe- and whitespace-insensitive. */
const normSearch = (s: string) =>
  s.toLowerCase().replace(/[’'`]/g, '').replace(/\s+/g, ' ').trim();

const toPacing = (c: CurrentCampaign) => ({
  percentDelivered: c.deliveryPercent,
  // GAM-native expected % (DeliveryIndicator rollup) — pre-Aug-2026 snapshots
  // only carry the time-linear flight progress.
  expectedPercent: c.expectedDeliveryPercent ?? c.flightProgressPercent,
  isPacingAhead: c.pacingStatus === 'AHEAD',
  isPacingBehind: c.pacingStatus === 'BEHIND',
});

const sectionHead: React.CSSProperties = {
  fontSize: 12, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase',
  color: 'var(--fg-2)', margin: '22px 0 3px',
};
const caption: React.CSSProperties = {
  fontSize: 12, color: 'var(--fg-4)', margin: '0 0 10px', lineHeight: 1.5, maxWidth: 820,
};
const banner: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, marginTop: 14, padding: '10px 14px',
  borderRadius: 'var(--radius-md)', fontSize: 12.5, lineHeight: 1.45,
};
const brandCard: React.CSSProperties = {
  background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)', marginTop: 16, overflow: 'hidden',
};
const brandHead: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap',
  padding: '12px 14px', borderBottom: '1px solid var(--border-1)', background: 'var(--cg-neutral-50)',
};
const chanPill: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px',
  borderRadius: 'var(--radius-pill)', fontSize: 11.5, background: 'var(--accent-soft)',
  color: 'var(--accent-soft-fg)', whiteSpace: 'nowrap',
};
const kpiGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginTop: 14,
};
const searchInput: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-1)',
  background: 'var(--bg-surface-1)', color: 'var(--fg-1)', fontSize: 13, minWidth: 260,
};

const CurrentCampaignsTab: React.FC = () => {
  const [snap, setSnap] = useState<CurrentCampaignsSnapshot | null>(null);
  const [channelIndex, setChannelIndex] = useState<CurrentCampaignsChannelIndex | null>(null);
  const [refreshInFlight, setRefreshInFlight] = useState(false);
  const [loading, setLoading] = useState(true); // first fetch only
  const [error, setError] = useState<string | null>(null);
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await getCurrentCampaigns();
      setSnap(r.snapshot);
      setChannelIndex(r.channelIndex);
      setRefreshInFlight(r.refreshInFlight);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load current campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while a refresh runs (paused when the browser tab is hidden).
  useEffect(() => {
    if (!refreshInFlight) return;
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [refreshInFlight, load]);

  const onRefresh = async () => {
    try {
      await refreshCurrentCampaigns();
      setRefreshInFlight(true); // 202 = job started (or one already runs) — start polling
    } catch (e: any) {
      setError(e?.message || 'Failed to start a refresh');
    }
  };

  const toggleChannels = (orderId: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const brands = snap?.brands || [];
  const totals = snap?.totals;
  const hasData = Boolean(snap?.fetchedAt);

  // Channel search runs over the UNTRUNCATED channel index doc, not the brand
  // cards' capped channel lists — a zero-match answer is definitive for the
  // window. Name-prefix matches float above mid-string matches.
  const q = normSearch(query);
  const searching = q.length > 0;
  const channelMatches = searching
    ? (channelIndex?.entries || [])
        .filter(
          (e) =>
            normSearch(e.channelName).includes(q) ||
            (e.channelId || '').toLowerCase() === query.trim().toLowerCase(),
        )
        .sort(
          (a, b) =>
            Number(normSearch(b.channelName).startsWith(q)) - Number(normSearch(a.channelName).startsWith(q)) ||
            b.impressions - a.impressions,
        )
    : [];
  const shownMatches = channelMatches.slice(0, MAX_CHANNEL_RESULTS);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, color: 'var(--fg-1)' }}>Current Campaigns</h2>
        <Button variant="subtle" size="sm" onClick={onRefresh} disabled={refreshInFlight}>
          {refreshInFlight ? 'Refreshing…' : 'Refresh now'}
        </Button>
        <input
          placeholder="🔍  Which brands are on… (channel name)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={searchInput}
          aria-label="Search by channel"
        />
        {query && (
          <Button variant="subtle" size="sm" onClick={() => setQuery('')}>
            Clear
          </Button>
        )}
        {hasData && (
          <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
            Data as of {new Date(snap!.fetchedAt!).toLocaleString()}
          </span>
        )}
      </div>
      <p style={{ ...caption, marginTop: 6 }}>
        Brands with line items <strong>delivering in Google Ad Manager right now</strong>. Channel lists show where each
        campaign <strong>actually delivered impressions</strong>
        {snap?.windowStart ? <> between <strong>{snap.windowStart}</strong> and <strong>{snap.windowEnd}</strong></> : <> over the last 7 days</>}
        {' '}(Direct Sold ad-server delivery — GAM can’t list channels for a campaign before it starts delivering).
        The snapshot refreshes automatically when it’s more than 24 hours old. Search a channel above to see every
        brand delivering on it.
      </p>

      {loading && <div style={{ marginTop: 24 }}><LoadingDots /></div>}
      {/* Suppress the blocking error banner while a refresh is in flight: a transient
          poll failure keeps the last snapshot on screen and retries next tick, so it
          must not contradict the "Refreshing…" banner. */}
      {error && !loading && !refreshInFlight && (
        <div style={{ ...banner, background: 'var(--danger-soft)', color: 'var(--danger)' }}>{error}</div>
      )}

      {/* Refresh / degradation banners */}
      {!loading && refreshInFlight && (
        <div style={{ ...banner, background: 'var(--accent-soft)', color: 'var(--accent-soft-fg)' }}>
          <Spinner size={14} />
          <span>
            Refreshing from Google Ad Manager — this takes a few minutes.
            {hasData ? ' Showing the last snapshot meanwhile.' : ' Building the first snapshot…'}
          </span>
        </div>
      )}
      {!loading && snap?.status === 'error' && snap.error && (
        <div style={{ ...banner, background: 'var(--danger-soft)', color: 'var(--danger)' }}>
          Last refresh failed: {snap.error}
          {hasData ? ` — showing data from ${new Date(snap.fetchedAt!).toLocaleString()}.` : ''}
        </div>
      )}
      {!loading && snap?.channelReportError && snap.status !== 'error' && (
        <div style={{ ...banner, background: 'var(--warning-soft)', color: 'var(--warning)' }}>
          Campaigns are current, but channel attribution failed on the last refresh ({snap.channelReportError}).
          Use “Refresh now” to retry.
        </div>
      )}

      {/* First-ever open: nothing cached yet, refresh building */}
      {!loading && !hasData && !refreshInFlight && !error && !searching && (
        <EmptyState
          title="No snapshot yet"
          subtitle="Use “Refresh now” to pull the current campaigns from Google Ad Manager."
        />
      )}

      {/* ---- Channel search results (replace the brand cards while a query is set) ---- */}
      {searching && !loading && !channelIndex && !refreshInFlight && (
        <div style={{ ...banner, background: 'var(--warning-soft)', color: 'var(--warning)' }}>
          The channel search index hasn’t been built yet — it’s generated by the next snapshot refresh. Use “Refresh
          now”, then search again.
        </div>
      )}
      {searching && !loading && channelIndex && (
        <>
          <div style={sectionHead}>
            Channel search · {channelMatches.length} match{channelMatches.length === 1 ? '' : 'es'}
          </div>
          <p style={caption}>
            Brands whose campaigns delivered impressions on matching channels
            {channelIndex.windowStart ? (
              <> between <strong>{channelIndex.windowStart}</strong> and <strong>{channelIndex.windowEnd}</strong></>
            ) : (
              <> in the last 7 days</>
            )}
            . Unlike the brand cards, this searches <strong>every</strong> channel with delivery in the window.
            {channelIndex.entriesTruncated > 0 &&
              ` Index limited to the top ${channelIndex.entries.length} of ${channelIndex.channelCount} channels by delivery.`}
          </p>
          {channelMatches.length === 0 && (
            <EmptyState
              title={`No delivery on channels matching “${query.trim()}”`}
              subtitle="No brand delivered Direct Sold impressions on a matching channel in this window — either nothing is running there right now, or GAM spells the channel name differently."
            />
          )}
          {shownMatches.map((entry: ChannelIndexEntry) => (
            <div key={entry.channelName} style={brandCard}>
              <div style={brandHead}>
                <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg-1)' }}>{entry.channelName}</span>
                {entry.channelId && (
                  <span style={{ fontSize: 11, color: 'var(--fg-4)', fontFamily: 'monospace' }}>{entry.channelId}</span>
                )}
                <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  {entry.brandCount} brand{entry.brandCount === 1 ? '' : 's'}
                  {' · '}{entry.campaignCount} campaign{entry.campaignCount === 1 ? '' : 's'}
                  {' · '}{fmtCompact(entry.impressions)} impressions in window
                </span>
              </div>
              <div style={tableStyles.wrapScroll}>
                <table style={tableStyles.table}>
                  <thead>
                    <tr>
                      <th style={tableStyles.th}>Brand</th>
                      <th style={tableStyles.thNum}>Campaigns on this channel</th>
                      <th style={tableStyles.thNum}>Impressions (window)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.brands.map((b) => (
                      <tr key={b.advertiserId}>
                        <td style={{ ...tableStyles.td, fontWeight: 600 }}>{b.advertiserName}</td>
                        <td style={tableStyles.tdNum}>{b.campaignCount}</td>
                        <td style={tableStyles.tdNum}>{fmtCompact(b.impressions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {entry.brandsTruncated && (
                <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--fg-4)' }}>
                  Top {entry.brands.length} of {entry.brandCount} brands shown (+
                  {fmtCompact(entry.otherBrandsImpressions)} impressions from the rest).
                </div>
              )}
            </div>
          ))}
          {channelMatches.length > shownMatches.length && (
            <p style={{ ...caption, marginTop: 10 }}>
              Showing the top {shownMatches.length} of {channelMatches.length} matching channels — refine the search to
              narrow it down.
            </p>
          )}
        </>
      )}

      {!searching && hasData && totals && (
        <div style={kpiGrid}>
          <KpiCard label="Active Brands" value={totals.brandCount} hint="Advertisers with delivering line items" />
          <KpiCard label="Active Campaigns" value={totals.campaignCount} hint="GAM orders with ≥1 delivering line item" />
          <KpiCard label="Channels w/ Delivery" value={totals.channelCount} hint="Distinct channels in the window" />
          <KpiCard label="Window Impressions" value={fmtCompact(totals.windowImpressions)} hint={`Delivered ${snap!.windowStart} → ${snap!.windowEnd}`} />
        </div>
      )}

      {/* Don't assert "no active campaigns" while a refresh is running: on a stale
          open the GET returns the old (status:'complete') snapshot alongside
          refreshInFlight:true, so key off the flag, not just the doc status. */}
      {!searching && hasData && brands.length === 0 && !refreshInFlight && snap!.status !== 'refreshing' && (
        <EmptyState title="No active campaigns" subtitle="No line items are currently delivering in Google Ad Manager." />
      )}

      {!searching && brands.map((brand: CurrentCampaignsBrand) => (
        <div key={brand.advertiserId} style={brandCard}>
          <div style={brandHead}>
            <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--fg-1)' }}>
              {brand.advertiserName}
              {!brand.advertiserNameResolved && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: 'var(--fg-4)' }}>(name unresolved)</span>
              )}
            </span>
            <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>
              {brand.campaignCount} campaign{brand.campaignCount === 1 ? '' : 's'}
              {' · '}{brand.channelCountTotal} channel{brand.channelCountTotal === 1 ? '' : 's'}
              {' · '}{fmtCompact(brand.windowImpressions)} impressions in window
            </span>
          </div>
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Campaign</th>
                  <th style={tableStyles.th}>Flight</th>
                  <th style={tableStyles.thNum}>Goal</th>
                  <th style={tableStyles.thNum}>Delivered</th>
                  <th style={tableStyles.th}>Pacing</th>
                  <th style={tableStyles.th}>Channels (delivery in window)</th>
                </tr>
              </thead>
              <tbody>
                {brand.campaigns.map((c) => {
                  const expanded = expandedChannels.has(c.gamOrderId);
                  const shown = expanded ? c.channels : c.channels.slice(0, CHANNEL_PILLS_COLLAPSED);
                  const hiddenStored = c.channels.length - shown.length;
                  const pill = PACING_PILL[c.pacingStatus] || PACING_PILL.ON_TRACK;
                  return (
                    <tr key={c.gamOrderId}>
                      <td style={{ ...tableStyles.td, minWidth: 200 }}>
                        <div style={{ fontWeight: 600 }}>{c.gamOrderName}</div>
                        <div style={{ fontSize: 11, color: 'var(--fg-4)' }}>
                          {c.ioOrderNumber ? `IO ${c.ioOrderNumber} · ` : ''}{c.lineItemCount} line item{c.lineItemCount === 1 ? '' : 's'}
                        </div>
                      </td>
                      {/* Dates stay as raw YYYY-MM-DD: Date-parsing them shifts a day in negative-UTC-offset timezones. */}
                      <td style={{ ...tableStyles.td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                        {c.startDate} → {c.endDate}
                      </td>
                      {/* Booked total stays the IO-reconciliation figure. When
                          deactivated lines make the pacing basis smaller, say so
                          on hover rather than leaving the bar looking wrong
                          beside it. */}
                      <td style={tableStyles.tdNum} title={goalCellTitle(c)}>
                        {c.totalImpressionsGoal > 0 ? fmtCompact(c.totalImpressionsGoal) : '—'}
                        {goalCellTitle(c) ? <span style={{ color: 'var(--fg-4)', marginLeft: 3 }}>*</span> : null}
                      </td>
                      <td style={tableStyles.tdNum}>{fmtCompact(c.totalImpressionsDelivered)}</td>
                      <td style={{ ...tableStyles.td, minWidth: 130 }}>
                        <PacingBar delivery={toPacing(c)} variant="compact" />
                        <div style={{ marginTop: 4 }}>
                          <StatusPill variant={pill.variant}>{pill.label}</StatusPill>
                        </div>
                      </td>
                      <td style={{ ...tableStyles.td, maxWidth: 460 }}>
                        {c.channels.length === 0 ? (
                          <span style={{ fontSize: 12, color: 'var(--fg-4)', fontStyle: 'italic' }}>
                            {c.windowImpressions > 0 ? 'Delivery not attributed to channels' : 'No delivery in window yet'}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {shown.map((ch) => (
                              <button
                                key={ch.channelName}
                                type="button"
                                onClick={() => setQuery(ch.channelName)}
                                style={{ ...chanPill, border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}
                                title={`${ch.channelId || 'Channel id unresolved'} — click to see every brand on this channel`}
                              >
                                {ch.channelName}
                                <span style={{ opacity: 0.75 }}>· {fmtCompact(ch.impressions)}</span>
                              </button>
                            ))}
                            {(hiddenStored > 0 || expanded) && (
                              <button
                                type="button"
                                onClick={() => toggleChannels(c.gamOrderId)}
                                style={{
                                  border: '1px solid var(--border-2)', background: 'var(--bg-surface-1)',
                                  color: 'var(--fg-3)', borderRadius: 'var(--radius-pill)',
                                  fontSize: 11.5, padding: '3px 9px', cursor: 'pointer',
                                }}
                              >
                                {expanded ? 'Show fewer' : `+${hiddenStored} more`}
                              </button>
                            )}
                          </div>
                        )}
                        {(c.channelsTruncated || c.unattributedImpressions > 0) && (
                          <div style={{ marginTop: 5, fontSize: 11, color: 'var(--fg-4)' }}>
                            {c.channelsTruncated &&
                              `Top ${c.channels.length} of ${c.channelCountTotal} channels shown (+${fmtCompact(c.otherChannelsImpressions)} impressions elsewhere). `}
                            {c.unattributedImpressions > 0 && `Unattributed: ${fmtCompact(c.unattributedImpressions)}.`}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {!searching && hasData && brands.length > 0 && (
        <>
          <div style={sectionHead}>📡 How to read this</div>
          <p style={caption}>
            “Goal” and “Delivered” are lifetime campaign figures from GAM line items; the pacing bar compares delivered %
            against flight progress. Channel chips are delivered impressions within the snapshot window only, attributed via
            GAM’s yt_channel_id dimension — a channel chip means that brand’s ads ran on that channel this week. Click a
            chip (or use the search box) to flip the view around: one channel, every brand delivering on it.
          </p>
        </>
      )}
    </div>
  );
};

export default CurrentCampaignsTab;
